import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import {
  menuSectionsForRole, superMenuSections, ROLE_LABELS, ROLE_BADGE_CLASS,
} from '@/lib/roles';
import { classNames, initials } from '@/lib/format';
import {
  LogOut, Sun, Moon, ChevronsLeft, ChevronsRight, Menu, AlertTriangle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import NotificationBell from '@/components/NotificationBell';
import api from '@/lib/api';

function NavList({ sections, collapsed, location, onNavigate, testPrefix = '', showLabels = false }) {
  return (
    <div className="space-y-0.5">
      {sections.map((sec, si) => (
        <div key={sec.title || si}>
          {showLabels && !collapsed && sec.title ? (
            <div className="nav-section-label">{sec.title}</div>
          ) : (
            si > 0 && (
              <div className="my-2.5 mx-2 h-px bg-[hsl(var(--sidebar-border))]" />
            )
          )}
          {sec.items.map((m) => {
            const Icon = m.icon;
            const active = location.pathname === m.path || location.pathname.startsWith(`${m.path}/`);
            const link = (
              <Link
                key={`${testPrefix}${m.key}`}
                to={m.path}
                data-testid={`${testPrefix}nav-${m.key}`}
                onClick={onNavigate}
                className={classNames(
                  'nav-item',
                  active && 'nav-item-active',
                  collapsed && 'justify-center px-0',
                )}
              >
                <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />
                {!collapsed && <span className="truncate">{m.label}</span>}
              </Link>
            );
            if (!collapsed) return link;
            return (
              <Tooltip key={`${testPrefix}${m.key}`}>
                <TooltipTrigger asChild>{link}</TooltipTrigger>
                <TooltipContent side="right">{m.label}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function AppShell({ children, isSuper = false }) {
  const { user, tenant, logout, setTheme } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [waBanner, setWaBanner] = useState(null);
  const role = user?.role;

  const sections = useMemo(
    () => (isSuper ? superMenuSections() : menuSectionsForRole(role)),
    [isSuper, role],
  );

  const theme = user?.theme || (typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light');
  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  // Full-height workspace (inbox / leads) — no max-width padding cage
  const fullHeight = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith('/app/inbox') && !p.includes('/templates')) return true;
    if (p === '/app/leads' || p.startsWith('/app/leads?')) return true;
    return false;
  }, [location.pathname]);

  useEffect(() => {
    if (isSuper) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get('/whatsapp/status');
        if (cancelled) return;
        const st = data?.session?.status || data?.qr_status;
        const mode = data?.mode;
        if (mode === 'qr' && st && st !== 'connected') {
          setWaBanner({ status: st });
        } else if (mode === 'none' || !data?.config_active) {
          setWaBanner({ status: 'unconfigured' });
        } else {
          setWaBanner(null);
        }
      } catch {
        /* ignore */
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [isSuper]);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-screen w-full overflow-hidden page-canvas text-foreground">
        {/* Sidebar */}
        <aside
          data-testid="app-sidebar"
          className={classNames(
            'hidden md:flex flex-col shell-sidebar transition-[width] duration-200 shrink-0',
            collapsed ? 'w-[68px]' : 'w-[240px]',
          )}
        >
          <div className="flex items-center gap-2.5 h-14 px-3 border-b border-[hsl(var(--sidebar-border))]">
            <div className="brand-mark h-8 w-8 rounded-xl text-xs">Z</div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold truncate text-[hsl(var(--sidebar-foreground))]">
                  {isSuper ? 'Zunoid' : (tenant?.name || 'CRM')}
                </div>
              </div>
            )}
            <button
              data-testid="sidebar-collapse-button"
              aria-label="Colapsar"
              className="h-7 w-7 grid place-items-center rounded-lg text-[hsl(var(--sidebar-muted))] hover:bg-[hsl(var(--sidebar-hover))]"
              onClick={() => setCollapsed((c) => !c)}
            >
              {collapsed ? <ChevronsRight className="h-3.5 w-3.5" /> : <ChevronsLeft className="h-3.5 w-3.5" />}
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-3 px-2.5">
            <NavList sections={sections} collapsed={collapsed} location={location} />
          </nav>

          <div className="p-2.5 border-t border-[hsl(var(--sidebar-border))]">
            {!collapsed ? (
              <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-[hsl(var(--sidebar-hover))]">
                <div className="h-8 w-8 rounded-full bg-accent text-primary grid place-items-center text-[11px] font-semibold">
                  {initials(user?.name || user?.email)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate text-[hsl(var(--sidebar-foreground))]">
                    {user?.name || user?.email}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-8 w-8 mx-auto rounded-full bg-accent text-primary grid place-items-center text-[11px] font-semibold">
                {initials(user?.name || user?.email)}
              </div>
            )}
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 shell-main">
          <header data-testid="app-topbar" className="shrink-0 z-30 h-14 border-b border-border glass">
            <div className="h-full px-3 sm:px-5 flex items-center gap-2">
              <div className="md:hidden">
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild>
                    <button data-testid="mobile-sidebar-trigger" className="btn-ghost h-9 w-9 p-0" aria-label="Menú">
                      <Menu className="h-4 w-4" />
                    </button>
                  </SheetTrigger>
                  <SheetContent side="left" className="p-0 w-[84vw] max-w-[300px]">
                    <div className="flex h-full flex-col shell-sidebar">
                      <div className="h-14 px-4 flex items-center gap-2.5 border-b border-[hsl(var(--sidebar-border))]">
                        <div className="brand-mark h-8 w-8 rounded-xl text-xs">Z</div>
                        <span className="text-sm font-semibold truncate">{tenant?.name || 'CRM'}</span>
                      </div>
                      <nav className="flex-1 overflow-y-auto py-3 px-2.5">
                        <NavList
                          sections={sections}
                          collapsed={false}
                          location={location}
                          onNavigate={() => setMobileOpen(false)}
                          testPrefix="mobile-"
                        />
                      </nav>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>

              <div className="flex-1" />

              {!isSuper && <NotificationBell />}

              <button
                data-testid="theme-toggle"
                onClick={toggleTheme}
                className="btn-ghost h-9 w-9 p-0"
                aria-label="Tema"
              >
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-trigger"
                    className="btn-secondary h-9 pl-1.5 pr-2.5 gap-2 shadow-none"
                  >
                    <div className="h-6 w-6 rounded-full bg-accent text-primary grid place-items-center text-[10px] font-semibold">
                      {initials(user?.name || user?.email)}
                    </div>
                    <span className={classNames(
                      'hidden sm:inline text-[10px] font-medium rounded-full px-1.5 py-0.5',
                      ROLE_BADGE_CLASS[role] || 'bg-muted text-muted-foreground',
                    )}
                    >
                      {ROLE_LABELS[role]}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 rounded-xl">
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-xs truncate">{user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="user-menu-theme" onClick={toggleTheme}>
                    Tema {theme === 'dark' ? 'claro' : 'oscuro'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="user-menu-logout" onClick={logout} className="text-[hsl(var(--danger))]">
                    <LogOut className="h-3.5 w-3.5 mr-2" /> Salir
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {!isSuper && waBanner && (
            <div className="banner-warn px-4 py-2 text-xs flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--warning))] shrink-0" />
              <span className="flex-1 truncate text-muted-foreground">
                {waBanner.status === 'unconfigured' ? 'WhatsApp sin configurar' : `WhatsApp: ${waBanner.status}`}
              </span>
              <Link to="/app/settings" className="font-medium text-foreground hover:underline shrink-0">
                Ajustes
              </Link>
            </div>
          )}

          <main
            className={classNames(
              'flex-1 min-w-0 min-h-0 flex flex-col',
              fullHeight ? 'overflow-hidden p-0' : 'overflow-auto p-4 sm:p-6 lg:p-8',
            )}
          >
            <div
              className={classNames(
                fullHeight
                  ? 'flex-1 min-h-0 flex flex-col w-full'
                  : 'mx-auto w-full max-w-[1280px]',
              )}
            >
              {children}
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
